import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { hydraUiApi } from '@/services/api';
import type { Geofence } from '@/services/db/geofence/entity';
import { useRequiredGeosetIdFromCurrentUrl } from '@/services/router/use-geoset-id';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import {
  geosetKeys,
  parseGeoset,
  parseGeosetFence,
  parseGeosetFencePoint,
  type Geoset,
  type GeosetFence,
  type GeosetFencePoint,
  type GeosetFencesListParams,
  type GeosetListParams,
  type GeosetUsage,
} from './entity';
import { geosetFenceToGeofence } from './fence-adapter';
import { parseGeosetsListResponse, populateGeosetsList, type ParsedGeosetsListResponse } from './populate';

const STALE_TIME = 60_000;

/** The list endpoint is unparameterized, so every list read shares one slot. */
const LIST_PARAMS: GeosetListParams = {};

function useValidWorkspaceId(): number | null {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  return workspaceId != null && workspaceId > 0 ? workspaceId : null;
}

/* ------------------------------------------------------------------------- */
/* listGeosets — the workspace's full geoset collection                       */
/* ------------------------------------------------------------------------- */

/** @public */
export async function listGeosets(workspaceId: number, qc: QueryClient): Promise<ParsedGeosetsListResponse> {
  const raw = await hydraUiApi.geosets.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const response = parseGeosetsListResponse(raw);
  populateGeosetsList(qc, workspaceId, response);
  return response;
}

export const listGeosetsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: geosetKeys.list(workspaceId, LIST_PARAMS),
    queryFn: () => listGeosets(workspaceId, qc),
    staleTime: STALE_TIME,
  });

interface UseGeosetsOptions {
  queryConfig?: Omit<
    UseQueryOptions<ParsedGeosetsListResponse, Error, ParsedGeosetsListResponse, readonly unknown[]>,
    'queryKey' | 'queryFn'
  >;
}

/**
 * Non-suspending read of the workspace's geosets for the geosets table.
 * Returns `data: undefined` until a workspace id resolves from the URL.
 */
/** @public */
export const useGeosets = ({ queryConfig }: UseGeosetsOptions = {}) => {
  const workspaceId = useValidWorkspaceId();
  const qc = useQueryClient();

  return useQuery<ParsedGeosetsListResponse, Error, ParsedGeosetsListResponse, readonly unknown[]>({
    queryKey: workspaceId != null ? geosetKeys.list(workspaceId, LIST_PARAMS) : ['geoset', 'no-workspace'],
    queryFn: workspaceId != null ? () => listGeosets(workspaceId, qc) : skipToken,
    staleTime: STALE_TIME,
    ...queryConfig,
  });
};

/**
 * Cheap workspace geoset count; `undefined` while loading or on error.
 * The list endpoint returns the whole collection, so the count is just the
 * length of that same cached read — no extra request. If a dedicated count
 * endpoint ever lands, swap the internals here; callers only see the number.
 *
 * @public
 */
export const useGeosetCount = (): number | undefined => {
  const workspaceId = useValidWorkspaceId();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: workspaceId != null ? geosetKeys.list(workspaceId, LIST_PARAMS) : ['geoset', 'no-workspace'],
    queryFn: workspaceId != null ? () => listGeosets(workspaceId, qc) : skipToken,
    staleTime: STALE_TIME,
  });
  return data?.geosets.length;
};

/* ------------------------------------------------------------------------- */
/* geoset usage — what references a single geoset                             */
/* ------------------------------------------------------------------------- */

/** @public */
export function geosetUsageQueryKey(workspaceId: number, geosetId: string) {
  return [...geosetKeys.detail(workspaceId, geosetId), 'usage'] as const;
}

/** @public */
export async function getGeosetUsage(workspaceId: number, geosetId: string): Promise<GeosetUsage> {
  const { usage } = await hydraUiApi.geosets.getUsage({
    params: { path: { environment_id: String(workspaceId), id: geosetId } },
  });
  // Normalize each list independently, not just a nullish whole `usage`: a
  // sparse payload (one list present, the other missing) would otherwise throw
  // on the `.length` reads in the Usage cell and delete guard.
  return { campaigns: usage?.campaigns ?? [], segments: usage?.segments ?? [] };
}

/** @public */
export const geosetUsageQueryOptions = (workspaceId: number, geosetId: string) =>
  queryOptions({
    queryKey: geosetUsageQueryKey(workspaceId, geosetId),
    queryFn: () => getGeosetUsage(workspaceId, geosetId),
    staleTime: STALE_TIME,
  });

/**
 * Usage for a single geoset. Fetched per row so the geosets table only reads
 * usage for the rows actually on screen, rather than the whole workspace.
 *
 * @public
 */
export const useGeosetUsage = (geosetId: string) => {
  const workspaceId = useValidWorkspaceId();
  const enabled = workspaceId != null;

  return useQuery({
    queryKey: enabled ? geosetUsageQueryKey(workspaceId, geosetId) : ['geoset', 'usage', 'no-workspace'],
    queryFn: enabled ? () => getGeosetUsage(workspaceId, geosetId) : skipToken,
    staleTime: STALE_TIME,
  });
};

/**
 * Reads already-cached usage for the given geosets, keyed by id, without
 * triggering any fetch — the per-row `useGeosetUsage` cells populate the cache,
 * and this observes it reactively. Powers the up-front delete guard so an
 * in-use geoset's Delete action can be disabled once its usage is known.
 *
 * @public
 */
export const useGeosetUsageByIdFromCache = (
  workspaceId: number,
  geosetIds: readonly string[],
): Record<string, GeosetUsage | undefined> => {
  const results = useQueries({
    queries: geosetIds.map((geosetId) => ({ ...geosetUsageQueryOptions(workspaceId, geosetId), enabled: false })),
  });

  return useMemo(() => {
    const byId: Record<string, GeosetUsage | undefined> = {};
    geosetIds.forEach((geosetId, index) => {
      byId[geosetId] = results[index]?.data;
    });
    return byId;
  }, [geosetIds, results]);
};

/* ------------------------------------------------------------------------- */
/* getGeoset — by-id detail                                                   */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getGeoset(workspaceId: number, geosetId: string, _qc: QueryClient): Promise<Geoset> {
  const data = await hydraUiApi.geosets.get({
    params: { path: { environment_id: String(workspaceId), id: geosetId } },
  });
  if (data.geoset == null) {
    throw new Error('missing geoset in get geoset response');
  }
  return parseGeoset(data.geoset);
}

/** @public */
export const getGeosetQueryOptions = (workspaceId: number, geosetId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: geosetKeys.detail(workspaceId, geosetId),
    queryFn: () => getGeoset(workspaceId, geosetId, qc),
    staleTime: STALE_TIME,
  });

/* ------------------------------------------------------------------------- */
/* listGeosetFences — one server-driven page of a geoset's fences             */
/* ------------------------------------------------------------------------- */

/**
 * Query key PREFIX for a geoset's fences. Every fences read — the per-params
 * page slots, the slim points read, and the per-fence details — nests under
 * it, so callers that mutate the underlying geofences (create/update/delete/
 * import) invalidate this one key and refresh them all.
 *
 * @public
 */
export function geosetFencesQueryKey(workspaceId: number, geosetId: string) {
  return [...geosetKeys.detail(workspaceId, geosetId), 'fences'] as const;
}

/** Query key for one `getFences` page; params are part of the key. @public */
export function geosetFencesPageQueryKey(workspaceId: number, geosetId: string, params: GeosetFencesListParams) {
  return [...geosetFencesQueryKey(workspaceId, geosetId), 'page', params] as const;
}

/** One page of a geoset's fences plus the pagination meta the table renders. */
export interface GeosetFencesPage {
  fences: GeosetFence[];
  meta: { page: number; pageSize: number; total: number };
}

/**
 * Fetch a single page of a geoset's fences. Search, sort, and paging all run
 * server-side (MBL-2147) — this replaces the old accumulate-the-whole-
 * membership loop, so the browser never downloads every full fence model.
 *
 * Pass a QueryClient to also seed the per-fence detail cache from the page's
 * rows, so focusing a fence the table already shows (row click → map
 * InfoWindow) resolves instantly without another GET.
 *
 * @public
 */
export async function listGeosetFences(
  workspaceId: number,
  geosetId: string,
  params: GeosetFencesListParams,
  qc?: QueryClient,
): Promise<GeosetFencesPage> {
  const raw = await hydraUiApi.geosets.getFences({
    params: {
      path: { environment_id: String(workspaceId), id: geosetId },
      query: {
        page: params.page,
        page_size: params.pageSize,
        sort: params.sort,
        order: params.order,
        search: params.search,
        bbox: params.bbox,
      },
    },
  });
  const fences = (raw.fences ?? []).map(parseGeosetFence);
  if (qc != null) {
    for (const fence of fences) {
      qc.setQueryData(geosetFenceDetailKey(workspaceId, geosetId, fence.id), geosetFenceToGeofence(fence));
    }
  }
  return {
    fences,
    // The contract requires meta, but guard a server omitting it: fall back to
    // the requested page/size and the returned row count so pagination renders
    // something sane instead of throwing.
    meta: {
      page: raw.meta?.page ?? params.page,
      pageSize: raw.meta?.page_size ?? params.pageSize,
      total: raw.meta?.total ?? fences.length,
    },
  };
}

/**
 * One server-driven page of the geoset's fences. `null` geosetId (or no
 * workspace) skips the fetch, so callers can pass the id straight from the URL
 * without a guard. Keeps the previous page's rows on params changes
 * (`keepPreviousData`) so the table doesn't blank out between round-trips.
 *
 * @public
 */
export const useGeosetFencesPage = (geosetId: string | null, params: GeosetFencesListParams) => {
  const workspaceId = useValidWorkspaceId();
  const qc = useQueryClient();
  const enabled = workspaceId != null && geosetId != null;

  return useQuery({
    queryKey: enabled ? geosetFencesPageQueryKey(workspaceId, geosetId, params) : ['geoset', 'fences', 'no-workspace'],
    queryFn: enabled ? () => listGeosetFences(workspaceId, geosetId, params, qc) : skipToken,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
};

/* ------------------------------------------------------------------------- */
/* listGeosetFencePoints — the slim full-set read the overview map clusters   */
/* ------------------------------------------------------------------------- */

/** Query key for the geoset's slim fence points. @public */
export function geosetFencePointsQueryKey(workspaceId: number, geosetId: string) {
  return [...geosetFencesQueryKey(workspaceId, geosetId), 'points'] as const;
}

/**
 * Fetch every fence's `{id, latitude, longitude, radius}` in one request —
 * the overview map clusters from this full set while the table pages
 * server-side.
 *
 * @public
 */
export async function listGeosetFencePoints(workspaceId: number, geosetId: string): Promise<GeosetFencePoint[]> {
  const raw = await hydraUiApi.geosets.getFencePoints({
    params: { path: { environment_id: String(workspaceId), id: geosetId } },
  });
  return (raw.points ?? []).map(parseGeosetFencePoint);
}

/**
 * The slim points for every fence in a geoset. `null` geosetId (or no
 * workspace) skips the fetch.
 *
 * @public
 */
export const useGeosetFencePoints = (geosetId: string | null) => {
  const workspaceId = useValidWorkspaceId();
  const enabled = workspaceId != null && geosetId != null;

  return useQuery({
    queryKey: enabled ? geosetFencePointsQueryKey(workspaceId, geosetId) : ['geoset', 'fence-points', 'no-workspace'],
    queryFn: enabled ? () => listGeosetFencePoints(workspaceId, geosetId) : skipToken,
    staleTime: STALE_TIME,
  });
};

interface UseGeosetOptions {
  id: string;
  queryConfig?: QueryConfig<typeof getGeosetQueryOptions>;
}

/** @public */
export const useGeoset = ({ id, queryConfig }: UseGeosetOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getGeosetQueryOptions(workspaceId, id, qc),
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* getGeosetFence — a single fence in a geoset, by id                         */
/* ------------------------------------------------------------------------- */

/**
 * Query key for a single geoset fence. Nested under the geoset's fences list
 * key so invalidating the list (after a create/update/delete) also drops the
 * cached detail.
 *
 * @public
 */
export function geosetFenceDetailKey(workspaceId: number, geosetId: string, fenceId: string) {
  return [...geosetFencesQueryKey(workspaceId, geosetId), fenceId] as const;
}

/**
 * Read a single fence in a geoset, adapted to the workspace `Geofence` shape
 * the geofences UI renders (see `geosetFenceToGeofence`).
 *
 * @public
 */
export async function getGeosetFence(workspaceId: number, geosetId: string, fenceId: string): Promise<Geofence> {
  const data = await hydraUiApi.geosets.getFencesByFence({
    params: { path: { environment_id: String(workspaceId), id: geosetId, fence_id: fenceId } },
  });
  if (data.fence == null) {
    throw new Error('missing fence in get geoset fence response');
  }
  return geosetFenceToGeofence(parseGeosetFence(data.fence));
}

/** @public */
export const getGeosetFenceQueryOptions = (workspaceId: number, geosetId: string, fenceId: string) =>
  queryOptions({
    queryKey: geosetFenceDetailKey(workspaceId, geosetId, fenceId),
    queryFn: () => getGeosetFence(workspaceId, geosetId, fenceId),
    staleTime: STALE_TIME,
  });

/**
 * Non-suspending read of a single fence's full model. `null` ids skip the
 * fetch, so callers can pass a nullable "focused fence" id straight through.
 * Table pages seed this cache (see `listGeosetFences`), so fences already on
 * screen resolve without a request.
 *
 * @public
 */
export const useGeosetFenceDetail = (geosetId: string | null, fenceId: string | null) => {
  const workspaceId = useValidWorkspaceId();
  const enabled = workspaceId != null && geosetId != null && fenceId != null;

  return useQuery({
    queryKey: enabled
      ? geosetFenceDetailKey(workspaceId, geosetId, fenceId)
      : ['geoset', 'fence-detail', 'no-workspace'],
    queryFn: enabled ? () => getGeosetFence(workspaceId, geosetId, fenceId) : skipToken,
    staleTime: STALE_TIME,
  });
};

interface UseGeosetFenceOptions {
  id: string;
  queryConfig?: QueryConfig<typeof getGeosetFenceQueryOptions>;
}

/**
 * Suspense read of a single fence in the geoset from the current URL. Mirrors
 * the shape of the retired `useGeofence` so the edit page consumes it the same
 * way — the geoset scope comes from the route.
 *
 * @public
 */
export const useGeosetFence = ({ id, queryConfig }: UseGeosetFenceOptions) => {
  const workspaceId = requireWorkspaceId();
  const geosetId = useRequiredGeosetIdFromCurrentUrl();
  return useSuspenseQuery({
    ...getGeosetFenceQueryOptions(workspaceId, geosetId, id),
    ...queryConfig,
  });
};
